import { Tabs, TabsContent, TabsList, TabsTrigger } from '@1trading/react';
import { useState } from 'react';
import { PageHeader } from '@/components/layout/page-header';
import { CoinFeesPanel } from '@/features/fees/coin-fees-panel';
import { CustomTradingFeesPanel } from '@/features/fees/custom-trading-fees-panel';
import { FeeExclusionsPanel } from '@/features/fees/fee-exclusions-panel';
import { useCryptoFees, useCustomTradingFees, useFeeExclusions, useFiatFees } from '@/hooks/queries/fees/use-fees';
import { getErrorMessage } from '@/lib/api/error';

type FeesTabId = 'fiat' | 'crypto' | 'exclusions' | 'trading';

const FeesPage = () => {
  const [activeTab, setActiveTab] = useState<FeesTabId>('fiat');

  // Everything fetches in parallel on mount (fees for the default tab,
  // exclusions + trading prefetched). The queries live at page level so tab
  // switches never unmount tables or blank cached data.
  const fiatQuery = useFiatFees();
  const cryptoQuery = useCryptoFees();
  const exclusionsQuery = useFeeExclusions();
  const tradingQuery = useCustomTradingFees();

  const handleTabChange = (value: string) => {
    const tab = value as FeesTabId;
    setActiveTab(tab);
    // (KEEP): trading fees may be edited elsewhere — ALWAYS refetch on
    // tab activation. Fiat/crypto/exclusions keep their cached data.
    if (tab === 'trading') void tradingQuery.refetch();
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader documentTitle="Fees - Bespoke Ops Portal - One Trading" title="Fees" />

      <Tabs onValueChange={handleTabChange} value={activeTab}>
        <TabsList>
          <TabsTrigger value="fiat">Fiat Fees</TabsTrigger>
          <TabsTrigger value="crypto">Crypto Fees</TabsTrigger>
          <TabsTrigger value="exclusions">Exclusions</TabsTrigger>
          <TabsTrigger value="trading">Trading Fees</TabsTrigger>
        </TabsList>

        <TabsContent className="mt-4" value="fiat">
          <CoinFeesPanel
            errorMessage={fiatQuery.isError ? `Failed to fetch fees: ${getErrorMessage(fiatQuery.error)}` : null}
            fees={fiatQuery.data}
            isLoading={fiatQuery.isPending}
            onRetry={() => void fiatQuery.refetch()}
          />
        </TabsContent>
        <TabsContent className="mt-4" value="crypto">
          <CoinFeesPanel
            errorMessage={cryptoQuery.isError ? `Failed to fetch fees: ${getErrorMessage(cryptoQuery.error)}` : null}
            fees={cryptoQuery.data}
            isLoading={cryptoQuery.isPending}
            onRetry={() => void cryptoQuery.refetch()}
          />
        </TabsContent>
        <TabsContent className="mt-4" value="exclusions">
          <FeeExclusionsPanel
            errorMessage={exclusionsQuery.isError ? `Failed to fetch fee exclusions: ${getErrorMessage(exclusionsQuery.error)}` : null}
            exclusions={exclusionsQuery.data}
            isLoading={exclusionsQuery.isPending}
            onRetry={() => void exclusionsQuery.refetch()}
          />
        </TabsContent>
        <TabsContent className="mt-4" value="trading">
          <CustomTradingFeesPanel
            errorMessage={tradingQuery.isError ? `Failed to fetch custom trading fees: ${getErrorMessage(tradingQuery.error)}` : null}
            fees={tradingQuery.data}
            isLoading={tradingQuery.isPending}
            onRetry={() => void tradingQuery.refetch()}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
FeesPage.displayName = 'FeesPage';

export default FeesPage;
